import React, { useContext, useEffect, useRef, useState } from 'react';
import { Box, useMediaQueries, useThemeContext } from '@honda-canada/design-system-react';
import { isEditorActive, withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';

import BottomElementContext from '../Footer/service/BottomElementProvider/BottomElementContext';
import DesktopSectionNav from './DesktopSectionNav';
import MobileSectionNav from './MobileSectionNav';
import {
  ACURA_SITE_NAME,
  ENGINE_SITE_NAME,
  HONDA_SITE_NAME,
  MARINE_SITE_NAME,
  PE_SITE_NAME,
  PSP_SITE_NAME,
} from '../../utils/constants';
import { useAppName } from '../../utils/sitecoreContext';

/**
 * TODO:
 * Implement Responsive Media
 * Make `route.fields` available for consumption in this repo locally
 * Fix Honda stickiness (see SideNavLayout)
 */

const checkIsNavInbound = (ref, window, stickyHeight) => {
  const componentOffsetTop = ref?.current?.offsetTop;
  const windowYOffset = window?.pageYOffset;
  return componentOffsetTop - windowYOffset <= parseInt(stickyHeight, 10);
};

// Ref to `useConfiguration` from `src/components/Header/service/utils.js`
const globalHeaderHeightByAppName = Object.freeze({
  [HONDA_SITE_NAME]: ['45px', '64px'],
  [ACURA_SITE_NAME]: ['45px', '64px'],
  [PSP_SITE_NAME]: ['45px', '104px'],
  [MARINE_SITE_NAME]: ['45px', '104px'],
  [PE_SITE_NAME]: ['45px', '64px'],
  [ENGINE_SITE_NAME]: ['45px', '75px'],
  default: ['45px', '75px'],
});

const SectionNavigation = ({ sitecoreContext, ...props }) => {
  const [isSticky, setIsSticky] = useState(false);
  const zIndices = useThemeContext('zIndices');
  const { isDesktop } = useMediaQueries();
  const { language, route } = sitecoreContext;
  const appName = useAppName();
  const [stickyHeightMobile, stickyHeightDesktop] =
    globalHeaderHeightByAppName[appName] ?? globalHeaderHeightByAppName.default;
  const stickyHeight = !isDesktop ? stickyHeightMobile : stickyHeightDesktop;

  const params = {
    language,
    pageName: route?.name,
    routeLabel: route?.fields?.sectionNavLabel,
  };

  const desktopRef = useRef(null);
  const mobileRef = useRef(null);
  const ref = !isDesktop ? mobileRef : desktopRef;

  const isSafeToAddEffects = typeof window !== 'undefined' && !isEditorActive();

  // set isSticky based on the current position, rather than always starting at false
  if (isSafeToAddEffects) {
    if (checkIsNavInbound(ref, window, stickyHeight) && !isSticky) {
      // FIXME: render function should be pure and not call state updater
      setIsSticky(true);
    }
  }

  useEffect(() => {
    if (isSafeToAddEffects) {
      const stickify = () => {
        const isNavInBound = checkIsNavInbound(ref, window, stickyHeight);
        if (isNavInBound && !isSticky) {
          setIsSticky(true);
        } else if (!isNavInBound && isSticky) {
          setIsSticky(false);
        }
      };

      window.addEventListener('scroll', stickify);
      return () => window.removeEventListener('scroll', stickify);
    }
    return null;
  }, [isSticky, ref]);

  // Not sticky in content editor
  const containerProps = isEditorActive()
    ? {
        position: 'relative',
      }
    : {
        position: 'sticky',
        top: 0,
        zIndex: isSticky ? zIndices.sectionNav : zIndices.subNavigationBar,
      };

  const { setDealerBannerHiddenOnScroll } = useContext(BottomElementContext);
  /*
    when SectionNav is scrolled just below the Global Header on the page, hide Dealer Banner
   */
  useEffect(() => {
    if (typeof setDealerBannerHiddenOnScroll === 'function' && !isDesktop) {
      setDealerBannerHiddenOnScroll(isSticky);
    }
    return () => {
      if (typeof setDealerBannerHiddenOnScroll === 'function') setDealerBannerHiddenOnScroll(false);
    };
  }, [isDesktop, isSticky]);

  return (
    <>
      <Box display={['none', 'block']} ref={desktopRef} {...containerProps}>
        <DesktopSectionNav {...props} params={params} />
      </Box>
      <Box display={['display', 'none']} ref={mobileRef} {...containerProps}>
        <MobileSectionNav {...props} params={params} />
      </Box>
    </>
  );
};

export default withSitecoreContext()(SectionNavigation);
